/**
 * The Mod branch chip on a project's row (RFC 0313 3j, canvas 6).
 *
 * Picks which branch this window reads in the project. Main by default, and
 * then it shows nothing but a quiet "main" on hover, so a project nobody
 * branches looks as it did. The list says what each branch holds; a branch
 * opens Compare from here, and the project's History opens from the same
 * menu, because what happened is a question about the branches, not one more
 * folder beside Sessions and Documents.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import { createBranch, projectBranches } from '@mod/core/model'
import { GitBranch, History } from 'lucide-react'
import { useState } from 'react'
import {
  setProjectBranch,
  useProjectBranch,
} from '../../services/branches/branchSelection'
import { readLocalPerson } from '../../services/identity/localPerson'
import { useDocument } from '../../services/world/repo/useDocument'
import { branchSubject } from './branchPane'

export function BranchChip({
  projectId,
  onOpen,
  onOpenHistory,
}: {
  projectId: string
  onOpen?: (file: ReturnType<typeof branchSubject>) => void
  /** Open the project's History (RFC 0313 3i). */
  onOpenHistory?: () => void
}) {
  const repo = useRepo() as unknown as Repo
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const current = useProjectBranch(projectId)
  const open = projectBranches(projectDoc).filter((b) => b.status === 'open')
  const [menu, setMenu] = useState(false)
  const [naming, setNaming] = useState<string | null>(null)

  return (
    <span className="relative">
      <button
        type="button"
        title={current ? `This window reads branch ${current}` : 'Branches'}
        onClick={(e) => {
          e.stopPropagation()
          setMenu((m) => !m)
        }}
        className={`flex items-center gap-1 rounded-full border px-1.5 text-[10px] ${
          current
            ? 'border-border text-foreground'
            : `border-border/60 text-muted-foreground ${
                menu
                  ? ''
                  : 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100'
              }`
        }`}
      >
        <GitBranch size={10} />
        {current || 'main'}
      </button>
      {menu ? (
        <>
          <button
            type="button"
            aria-label="Close branches"
            className="fixed inset-0 z-10 cursor-default"
            onClick={(e) => {
              e.stopPropagation()
              setMenu(false)
            }}
          />
          <div
            className="absolute right-0 z-20 mt-1 w-64 rounded-xl border border-border bg-background py-1 text-xs shadow-lg"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="menu"
          >
            <button
              type="button"
              onClick={() => {
                setProjectBranch(projectId, '')
                setMenu(false)
              }}
              className={`flex w-full items-center px-3 py-1.5 text-left hover:bg-muted ${current ? '' : 'bg-muted'}`}
            >
              main
              <span className="ml-auto text-muted-foreground">
                what everyone sees
              </span>
            </button>
            {open.map((b) => (
              <div
                key={b.name}
                className={`flex items-center gap-2 px-3 py-1.5 hover:bg-muted ${current === b.name ? 'bg-muted' : ''}`}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left"
                  onClick={() => {
                    setProjectBranch(projectId, b.name)
                    setMenu(false)
                  }}
                >
                  {b.name}
                </button>
                <span className="text-muted-foreground">
                  {Object.keys(b.copies).length} docs
                </span>
                {onOpen ? (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:underline"
                    onClick={() => {
                      onOpen(branchSubject(projectId, b.name))
                      setMenu(false)
                    }}
                  >
                    Compare
                  </button>
                ) : null}
              </div>
            ))}
            <div className="my-1 border-border border-t" />
            {onOpenHistory ? (
              <button
                type="button"
                onClick={() => {
                  onOpenHistory()
                  setMenu(false)
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
              >
                <History size={12} className="text-muted-foreground" />
                History
                <span className="ml-auto text-muted-foreground">
                  everything that happened
                </span>
              </button>
            ) : null}
            {naming === null ? (
              <button
                type="button"
                onClick={() => setNaming('')}
                className="block w-full px-3 py-1.5 text-left text-muted-foreground hover:bg-muted"
              >
                New branch from main
              </button>
            ) : (
              <form
                className="px-2 py-1"
                onSubmit={(e) => {
                  e.preventDefault()
                  const name = naming.trim()
                  if (!name) return
                  void createBranch(repo, projectId, {
                    name,
                    by: readLocalPerson()?.id ?? 'user',
                  })
                    .then(() => setProjectBranch(projectId, name))
                    .finally(() => {
                      setNaming(null)
                      setMenu(false)
                    })
                }}
              >
                <input
                  // biome-ignore lint/a11y/noAutofocus: they asked to name it
                  autoFocus
                  value={naming}
                  onChange={(e) => setNaming(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setNaming(null)}
                  placeholder="Branch name"
                  className="w-full rounded-md border border-border bg-transparent px-2 py-0.5 outline-none"
                />
              </form>
            )}
          </div>
        </>
      ) : null}
    </span>
  )
}
